import React, { useState } from "react";
import axios from "axios";
import "./Login.css";

const API_BASE = "https://api.seritonex.com";

const decodeJwt = (token) => {
  if (!token) return null;

  try {
    const base64Url = token.split(".")[1] || "";
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`;
        })
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (err) {
    console.error("JWT Decode Error:", err);
    return null;
  }
};

function mergePermissions(tokenClaims) {
  // Step 1 — collect group permissions into a map: code → permission object
  const merged = new Map();

  for (const group of tokenClaims?.groups ?? []) {
    for (const perm of group.permissions ?? []) {
      if (!perm.code) continue;
      const existing = merged.get(perm.code);
      if (!existing) {
        merged.set(perm.code, { ...perm });
      } else if (perm.allowed === 1 || perm.allowed === true) {
        // Permissive merge: if any group grants it, mark as granted.
        merged.set(perm.code, { ...perm, allowed: 1 });
      }
    }
  }

  // Step 1b — legacy flat permissions array (same priority as group)
  for (const perm of tokenClaims?.permissions ?? []) {
    if (!perm.code) continue;
    if (!merged.has(perm.code)) {
      merged.set(perm.code, { ...perm });
    } else if (perm.allowed === 1 || perm.allowed === true) {
      merged.set(perm.code, { ...merged.get(perm.code), allowed: 1 });
    }
  }

  // Step 2 — explicit user-level permissions unconditionally overwrite
  for (const perm of tokenClaims?.explicit_permissions ?? []) {
    if (!perm.code) continue;
    // Overwrite entirely — explicit value is the truth
    merged.set(perm.code, { ...perm });
  }

  return Array.from(merged.values());
}

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      const resp = await axios.post(`${API_BASE}/api/v1/amis/user/login`, {
        email: email.trim(),
        password,
      });

      const token = resp.data?.token;

      if (!token) {
        throw new Error("Login failed: no token received.");
      }

      const tokenClaims = decodeJwt(token) || {};
      const apiData = resp.data || {};

      // Build the final merged permission array:
      //   group permissions as the base, explicit user permissions as overrides.
      const resolvedPermissions = mergePermissions(tokenClaims);

      const userObject = {
        // Merge everything from token + api response
        ...tokenClaims,
        ...apiData,

        // Helpful aliases
        id: tokenClaims.user_id || apiData.user_id || apiData.id || null,

        email:
          tokenClaims.email_address ||
          tokenClaims.email ||
          apiData.email_address ||
          apiData.email ||
          email.trim(),

        organization_id:
          tokenClaims.organization?.organization_id ||
          apiData.organization?.organization_id ||
          null,

        organization_name:
          tokenClaims.organization?.name || apiData.organization?.name || "",

        // First group shortcut
        group: tokenClaims.groups?.[0]?.name || "",
        group_id: tokenClaims.groups?.[0]?.group_id || null,

        // Resolved permissions: group base + explicit overrides applied.
        // This is what non-context consumers should read.
        permissions: resolvedPermissions,

        // Preserve original arrays so PermissionsContext can rebuild from JWT
        explicit_permissions: tokenClaims.explicit_permissions ?? [],

        // Keep original token claims for reference
        tokenClaims,
      };

      localStorage.setItem("token", token);
      sessionStorage.setItem("authToken", token);

      localStorage.setItem("user", JSON.stringify(userObject));
      sessionStorage.setItem("user", JSON.stringify(userObject));

      localStorage.setItem("lastUserEmail", email.trim());

      sessionStorage.setItem("userEmail", userObject.email || "");
      sessionStorage.setItem("userId", String(userObject.id || ""));
      sessionStorage.setItem("userName", userObject.name || "");
      sessionStorage.setItem("userSurname", userObject.surname || "");
      sessionStorage.setItem("userGroup", userObject.group || "");
      sessionStorage.setItem(
        "organizationId",
        String(userObject.organization_id || ""),
      );
      sessionStorage.setItem("branchId", String(userObject.branch_id || ""));
      sessionStorage.setItem(
        "warehouseId",
        String(userObject.warehouse_id || ""),
      );
      sessionStorage.setItem("isLoggedIn", "true");

      if (onLogin) {
        onLogin(userObject);
      }
    } catch (err) {
      console.error(err);

      if (err.response?.status === 401) {
        setError("Incorrect email or password.");
      } else {
        setError(err.response?.data?.message || err.message || "Login failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = handleLogin;

  return (
    <div className="lp-root">
      <div className="lp-brand">
        <h1 className="lp-brand-heading">
          BETA
          <br />
          <span>Built to perform.</span>
        </h1>

        <div className="lp-brand-stats">
          <div className="lp-stat">
            <span className="lp-stat-val">Order</span>
            <span className="lp-stat-lbl">Real Time Quotes</span>
          </div>

          <div className="lp-stat">
            <span className="lp-stat-val">Live</span>
            <span className="lp-stat-lbl">Reporting</span>
          </div>

          <div className="lp-stat">
            <span className="lp-stat-val">Track</span>
            <span className="lp-stat-lbl">Live Tracking on active Gas</span>
          </div>
        </div>
      </div>

      <div className="lp-form-panel">
        <div className="lp-card">
          {/* Header */}
          <div className="lp-card-header">
            <p className="lp-eyebrow">Welcome back</p>

            <h2 className="lp-card-title">Sign In</h2>

            <p className="lp-card-sub">Sign in to access your dashboard.</p>
          </div>

          {/* Error */}
          {error && (
            <div className="lp-error" role="alert">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>

              {error}
            </div>
          )}

          {/* Form */}
          <div className="lp-form">
            {/* Email */}
            <div className="lp-field">
              <label className="fl" htmlFor="email">
                Email address
              </label>

              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="fi"
                autoComplete="email"
                onKeyDown={(e) =>
                  e.key === "Enter" && !loading && handleSubmit()
                }
              />
            </div>

            {/* Password */}
            <div className="lp-field">
              <label className="fl" htmlFor="password">
                Password
              </label>

              <div className="lp-pw-wrap">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="fi lp-pw-input"
                  autoComplete="current-password"
                  onKeyDown={(e) =>
                    e.key === "Enter" && !loading && handleSubmit()
                  }
                />

                <button
                  type="button"
                  className="lp-pw-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    <i className="bi bi-eye-slash"></i>
                  ) : (
                    <i className="bi bi-eye"></i>
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="lp-btn"
            >
              {loading && <span className="lp-spinner" />}

              {loading ? "Working…" : "Sign In"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
